import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';
import { aiServerClient } from '@edusense/ai-chat/services/aiserver-client';
import { useAuth } from '@edusense/shared-auth-core';
import { ConversationMessageRequestRole } from '@edusense/ai-client';
const toDate = (value)=>value ? new Date(value) : undefined;
function mapConversationSummary(dto, systemPrompt) {
    return {
        id: dto.id,
        title: dto.title?.trim() || '새로운 대화',
        messages: [],
        created_at: toDate(dto.createdAt) ?? new Date(),
        updated_at: toDate(dto.updatedAt) ?? new Date(),
        metadata: {
            model: dto.model ?? undefined,
            system_prompt: systemPrompt
        },
        messageCount: dto.messageCount ?? 0,
        lastMessagePreview: dto.lastMessagePreview ?? undefined,
        lastMessageRole: dto.lastMessageRole ? dto.lastMessageRole.toLowerCase() : undefined,
        lastMessageAt: toDate(dto.lastMessageAt ?? undefined)
    };
}
function mapConversationMessages(messages) {
    if (!messages || messages.length === 0) {
        return [];
    }
    return messages.map((message, index)=>{
        const role = message.role?.toLowerCase() || 'assistant';
        return {
            id: message.id ?? `msg-${index}-${Date.now()}`,
            content: message.content ?? '',
            role,
            timestamp: message.createdAt ? new Date(message.createdAt) : new Date(),
            metadata: {
                model: message.model ?? undefined,
                tokens: message.totalTokens ?? undefined
            }
        };
    });
}
function mapConversationDetail(dto, systemPrompt) {
    const summary = mapConversationSummary(dto, systemPrompt);
    const messages = mapConversationMessages(dto.messages);
    summary.messages = messages;
    summary.messageCount = messages.length;
    if (messages.length > 0) {
        const last = messages[messages.length - 1];
        summary.lastMessagePreview = last.content;
        summary.lastMessageRole = last.role;
        summary.lastMessageAt = last.timestamp;
    }
    return summary;
}
function buildChatUpdateFromSummary(dto) {
    const updates = {
        title: dto.title?.trim() || '새로운 대화',
        updated_at: toDate(dto.updatedAt) ?? new Date(),
        messageCount: dto.messageCount ?? 0,
        lastMessagePreview: dto.lastMessagePreview ?? undefined,
        lastMessageRole: dto.lastMessageRole ? dto.lastMessageRole.toLowerCase() : undefined,
        lastMessageAt: toDate(dto.lastMessageAt ?? undefined)
    };
    if (dto.model) {
        updates.metadata = {
            model: dto.model
        };
    }
    return updates;
}
// Initial state
const initialState = {
    chats: [],
    currentChatId: null,
    isLoading: false,
    isGenerating: false,
    currentModel: 'gpt-oss:20b',
    systemPrompt: ''
};
// Reducer
function chatReducer(state, action) {
    switch(action.type){
        case 'SET_LOADING':
            return {
                ...state,
                isLoading: action.payload
            };
        case 'SET_GENERATING':
            return {
                ...state,
                isGenerating: action.payload
            };
        case 'SET_CHATS':
            return {
                ...state,
                chats: action.payload
            };
        case 'ADD_CHAT':
            return {
                ...state,
                chats: [
                    action.payload,
                    ...state.chats
                ],
                currentChatId: action.payload.id
            };
        case 'UPDATE_CHAT':
            return {
                ...state,
                chats: state.chats.map((chat)=>chat.id === action.payload.id ? (()=>{
                        const mergedMetadata = action.payload.updates.metadata ? {
                            ...chat.metadata,
                            ...action.payload.updates.metadata
                        } : chat.metadata;
                        const updatedAt = action.payload.updates.updated_at ?? new Date();
                        return {
                            ...chat,
                            ...action.payload.updates,
                            metadata: mergedMetadata,
                            updated_at: updatedAt
                        };
                    })() : chat)
            };
        case 'DELETE_CHAT':
            const newChats = state.chats.filter((chat)=>chat.id !== action.payload);
            return {
                ...state,
                chats: newChats,
                currentChatId: state.currentChatId === action.payload ? newChats.length > 0 ? newChats[0].id : null : state.currentChatId
            };
        case 'SELECT_CHAT':
            return {
                ...state,
                currentChatId: action.payload
            };
        case 'ADD_MESSAGE':
            return {
                ...state,
                chats: state.chats.map((chat)=>chat.id === action.payload.chatId ? {
                        ...chat,
                        messages: [
                            ...chat.messages,
                            action.payload.message
                        ],
                        messageCount: (chat.messageCount ?? chat.messages.length) + 1,
                        lastMessagePreview: action.payload.message.content,
                        lastMessageRole: action.payload.message.role,
                        lastMessageAt: action.payload.message.timestamp,
                        updated_at: new Date()
                    } : chat)
            };
        case 'UPDATE_MESSAGE':
            return {
                ...state,
                chats: state.chats.map((chat)=>chat.id === action.payload.chatId ? {
                        ...chat,
                        messages: chat.messages.map((msg)=>msg.id === action.payload.messageId ? {
                                ...msg,
                                ...action.payload.updates
                            } : msg),
                        lastMessagePreview: (()=>{
                            const newMessages = chat.messages.map((msg)=>msg.id === action.payload.messageId ? {
                                    ...msg,
                                    ...action.payload.updates
                                } : msg);
                            const last = newMessages[newMessages.length - 1];
                            return last?.content;
                        })(),
                        lastMessageRole: (()=>{
                            const newMessages = chat.messages.map((msg)=>msg.id === action.payload.messageId ? {
                                    ...msg,
                                    ...action.payload.updates
                                } : msg);
                            const last = newMessages[newMessages.length - 1];
                            return last?.role;
                        })(),
                        lastMessageAt: (()=>{
                            const newMessages = chat.messages.map((msg)=>msg.id === action.payload.messageId ? {
                                    ...msg,
                                    ...action.payload.updates
                                } : msg);
                            const last = newMessages[newMessages.length - 1];
                            return last?.timestamp;
                        })(),
                        updated_at: new Date()
                    } : chat)
            };
        case 'DELETE_MESSAGE':
            return {
                ...state,
                chats: state.chats.map((chat)=>chat.id === action.payload.chatId ? {
                        ...chat,
                        messages: chat.messages.filter((msg)=>msg.id !== action.payload.messageId),
                        messageCount: Math.max(0, (chat.messageCount ?? chat.messages.length) - 1),
                        lastMessagePreview: (()=>{
                            const remaining = chat.messages.filter((msg)=>msg.id !== action.payload.messageId);
                            const last = remaining[remaining.length - 1];
                            return last?.content;
                        })(),
                        lastMessageRole: (()=>{
                            const remaining = chat.messages.filter((msg)=>msg.id !== action.payload.messageId);
                            const last = remaining[remaining.length - 1];
                            return last?.role;
                        })(),
                        lastMessageAt: (()=>{
                            const remaining = chat.messages.filter((msg)=>msg.id !== action.payload.messageId);
                            const last = remaining[remaining.length - 1];
                            return last?.timestamp;
                        })(),
                        updated_at: new Date()
                    } : chat)
            };
        case 'SET_MODEL':
            return {
                ...state,
                currentModel: action.payload
            };
        case 'SET_SYSTEM_PROMPT':
            return {
                ...state,
                systemPrompt: action.payload
            };
        case 'CLEAR_CHATS':
            return {
                ...state,
                chats: [],
                currentChatId: null
            };
        default:
            return state;
    }
}
// Context
const ChatContext = /*#__PURE__*/ createContext(undefined);
// Provider component
export function ChatProvider({ children }) {
    const [state, dispatch] = useReducer(chatReducer, initialState);
    const messageIdToUpdate = useRef(null);
    const { user } = useAuth();
    const resolvedUserId = user?.id ?? 'anonymous';
    useEffect(()=>{
        aiServerClient.setDefaultUser(resolvedUserId);
    }, [
        resolvedUserId
    ]);
    // Chat management
    const createChat = useCallback(async (title)=>{
        dispatch({
            type: 'SET_LOADING',
            payload: true
        });
        try {
            const dto = await aiServerClient.createConversation({
                title: title?.trim() || undefined,
                model: state.currentModel
            }, {
                userId: resolvedUserId
            });
            const mapped = mapConversationSummary(dto, state.systemPrompt);
            dispatch({
                type: 'ADD_CHAT',
                payload: mapped
            });
            return mapped.id;
        } catch (error) {
            console.error('Failed to create conversation:', error);
            throw error;
        } finally{
            dispatch({
                type: 'SET_LOADING',
                payload: false
            });
        }
    }, [
        resolvedUserId,
        state.currentModel,
        state.systemPrompt
    ]);
    const deleteChat = useCallback(async (chatId)=>{
        try {
            await aiServerClient.deleteConversation(chatId);
            dispatch({
                type: 'DELETE_CHAT',
                payload: chatId
            });
        } catch (error) {
            console.error('Failed to delete conversation:', error);
            throw error;
        }
    }, []);
    const clearChatMessages = useCallback(async (chatId)=>{
        dispatch({
            type: 'SET_LOADING',
            payload: true
        });
        try {
            const summary = await aiServerClient.clearConversationMessages(chatId);
            const updates = {
                ...buildChatUpdateFromSummary(summary),
                messages: []
            };
            dispatch({
                type: 'UPDATE_CHAT',
                payload: {
                    id: chatId,
                    updates
                }
            });
        } catch (error) {
            console.error('Failed to clear conversation messages:', error);
            throw error;
        } finally{
            dispatch({
                type: 'SET_LOADING',
                payload: false
            });
        }
    }, []);
    const loadConversation = useCallback(async (chatId, force = false)=>{
        const existing = state.chats.find((chat)=>chat.id === chatId);
        const needsFetch = force || !existing || existing.messages.length === 0 && (existing.messageCount ?? 0) > 0;
        if (!needsFetch && existing) {
            return existing;
        }
        try {
            const dto = await aiServerClient.getConversation(chatId);
            const mapped = mapConversationDetail(dto, state.systemPrompt);
            dispatch({
                type: 'UPDATE_CHAT',
                payload: {
                    id: chatId,
                    updates: mapped
                }
            });
            return mapped;
        } catch (error) {
            console.error('Failed to load conversation:', error);
            return existing ?? null;
        }
    }, [
        state.chats,
        state.systemPrompt
    ]);
    const selectChat = useCallback((chatId)=>{
        dispatch({
            type: 'SELECT_CHAT',
            payload: chatId
        });
        void loadConversation(chatId);
    }, [
        loadConversation
    ]);
    const updateChatTitle = useCallback(async (chatId, title)=>{
        const trimmed = title?.trim();
        if (!trimmed) {
            return;
        }
        try {
            const dto = await aiServerClient.updateConversationTitle(chatId, trimmed);
            const updates = buildChatUpdateFromSummary(dto);
            updates.title = dto.title ?? trimmed;
            dispatch({
                type: 'UPDATE_CHAT',
                payload: {
                    id: chatId,
                    updates
                }
            });
        } catch (error) {
            console.error('Failed to update conversation title:', error);
        }
    }, []);
    const clearChats = useCallback(async ()=>{
        const ids = state.chats.map((chat)=>chat.id);
        try {
            await Promise.all(ids.map((id)=>aiServerClient.deleteConversation(id).catch((err)=>{
                    console.warn('Failed to delete conversation during clear:', err);
                })));
        } finally{
            dispatch({
                type: 'CLEAR_CHATS'
            });
        }
    }, [
        state.chats
    ]);
    // Message management
    const sendMessage = useCallback(async (rawContent, _files)=>{
        const content = rawContent.trim();
        if (!content) {
            return;
        }
        dispatch({
            type: 'SET_GENERATING',
            payload: true
        });
        let chatId = state.currentChatId;
        let chatSnapshot = null;
        let stableChatId = chatId;
        const userIdForRequest = resolvedUserId;
        try {
            if (!chatId) {
                chatId = await createChat(content);
                stableChatId = chatId;
                chatSnapshot = await loadConversation(chatId, true);
            } else {
                chatSnapshot = await loadConversation(chatId);
                stableChatId = chatId;
            }
            if (!chatId) {
                throw new Error('Chat ID missing after ensure phase');
            }
            if (!chatSnapshot) {
                chatSnapshot = state.chats.find((chat)=>chat.id === chatId) ?? null;
            }
            const timestamp = new Date();
            const userMessage = {
                id: `msg-${timestamp.getTime()}-user`,
                content,
                role: 'user',
                timestamp
            };
            dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                    chatId,
                    message: userMessage
                }
            });
            const aiMessageId = `msg-${timestamp.getTime()}-ai`;
            const aiMessage = {
                id: aiMessageId,
                content: '',
                role: 'assistant',
                timestamp: new Date(),
                metadata: {
                    model: state.currentModel
                }
            };
            dispatch({
                type: 'ADD_MESSAGE',
                payload: {
                    chatId,
                    message: aiMessage
                }
            });
            messageIdToUpdate.current = aiMessageId;
            try {
                await aiServerClient.addConversationMessage(chatId, {
                    role: ConversationMessageRequestRole.USER,
                    content,
                    model: state.currentModel
                });
            } catch (persistError) {
                console.error('Failed to persist user message:', persistError);
            }
            const history = [
                ...chatSnapshot?.messages ?? [],
                userMessage
            ];
            const chatMessages = history.map((msg)=>({
                    role: msg.role,
                    content: msg.content
                }));
            if (state.systemPrompt) {
                chatMessages.unshift({
                    role: 'system',
                    content: state.systemPrompt
                });
            }
            let fullContent = '';
            const startTime = Date.now();
            try {
                for await (const chunk of aiServerClient.streamChat({
                    model: state.currentModel,
                    messages: chatMessages
                }, {
                    conversationId: chatId,
                    userId: userIdForRequest
                })){
                    if (chunk.done) {
                        break;
                    }
                    const piece = chunk.message?.content || '';
                    if (piece) {
                        fullContent += piece;
                        if (messageIdToUpdate.current === aiMessageId) {
                            dispatch({
                                type: 'UPDATE_MESSAGE',
                                payload: {
                                    chatId,
                                    messageId: aiMessageId,
                                    updates: {
                                        content: fullContent
                                    }
                                }
                            });
                        }
                    }
                }
            } catch (streamError) {
                console.error('❌ ChatContext: Stream error:', streamError);
                const fallbackResponse = '지금은 응답을 생성하지 못했어요. 잠시 후 다시 시도해 주세요.';
                fullContent = fallbackResponse;
                if (messageIdToUpdate.current === aiMessageId) {
                    dispatch({
                        type: 'UPDATE_MESSAGE',
                        payload: {
                            chatId,
                            messageId: aiMessageId,
                            updates: {
                                content: fallbackResponse
                            }
                        }
                    });
                }
            }
            const endTime = Date.now();
            dispatch({
                type: 'UPDATE_MESSAGE',
                payload: {
                    chatId,
                    messageId: aiMessageId,
                    updates: {
                        content: fullContent,
                        metadata: {
                            model: state.currentModel,
                            processing_time: endTime - startTime
                        }
                    }
                }
            });
            if (!chatSnapshot || (chatSnapshot.messageCount ?? 0) === 0) {
                const title = content.length > 50 ? `${content.substring(0, 47)}...` : content;
                await updateChatTitle(chatId, title);
            }
            if (fullContent.trim().length > 0) {
                try {
                    await aiServerClient.addConversationMessage(chatId, {
                        role: ConversationMessageRequestRole.ASSISTANT,
                        content: fullContent,
                        model: state.currentModel
                    });
                } catch (persistError) {
                    console.error('Failed to persist assistant message:', persistError);
                }
            }
            await loadConversation(chatId, true);
        } catch (error) {
            console.error('Failed to send message:', error);
            if (stableChatId) {
                const errorMessage = {
                    id: `msg-${Date.now()}-error`,
                    content: `오류: ${error instanceof Error ? error.message : '메시지 전송 중 오류가 발생했습니다.'}`,
                    role: 'assistant',
                    timestamp: new Date()
                };
                dispatch({
                    type: 'ADD_MESSAGE',
                    payload: {
                        chatId: stableChatId,
                        message: errorMessage
                    }
                });
            }
        } finally{
            dispatch({
                type: 'SET_GENERATING',
                payload: false
            });
        }
    }, [
        resolvedUserId,
        state.currentChatId,
        state.currentModel,
        state.chats,
        state.systemPrompt,
        createChat,
        loadConversation,
        updateChatTitle
    ]);
    const regenerateMessage = useCallback(async (messageId)=>{
        // TODO: Implement message regeneration
        console.log('Regenerating message:', messageId);
    }, []);
    const deleteMessage = useCallback(async (messageId)=>{
        const chatId = state.currentChatId;
        if (!chatId) return;
        dispatch({
            type: 'DELETE_MESSAGE',
            payload: {
                chatId,
                messageId
            }
        });
    }, [
        state.currentChatId
    ]);
    const stopGeneration = useCallback(()=>{
        dispatch({
            type: 'SET_GENERATING',
            payload: false
        });
        aiServerClient.stopGeneration();
        messageIdToUpdate.current = null;
    }, []);
    // Settings
    const setModel = useCallback((model)=>{
        dispatch({
            type: 'SET_MODEL',
            payload: model
        });
        localStorage.setItem('chat-model', model);
    }, []);
    const setSystemPrompt = useCallback((prompt)=>{
        dispatch({
            type: 'SET_SYSTEM_PROMPT',
            payload: prompt
        });
        localStorage.setItem('chat-system-prompt', prompt);
    }, []);
    // Utilities
    const getCurrentChat = useCallback(()=>{
        if (!state.currentChatId) return null;
        return state.chats.find((chat)=>chat.id === state.currentChatId) || null;
    }, [
        state.currentChatId,
        state.chats
    ]);
    const getAllChats = useCallback(()=>{
        return [
            ...state.chats
        ].sort((a, b)=>b.updated_at.getTime() - a.updated_at.getTime());
    }, [
        state.chats
    ]);
    const exportChat = useCallback(async (chatId)=>{
        const chat = state.chats.find((c)=>c.id === chatId);
        if (!chat) throw new Error('Chat not found');
        return JSON.stringify(chat, null, 2);
    }, [
        state.chats
    ]);
    const importChat = useCallback(async (data)=>{
        try {
            const chat = JSON.parse(data);
            // Generate new ID to avoid conflicts
            chat.id = `chat-${Date.now()}-imported`;
            chat.created_at = new Date();
            chat.updated_at = new Date();
            dispatch({
                type: 'ADD_CHAT',
                payload: chat
            });
        } catch (error) {
            throw new Error('Invalid chat data');
        }
    }, []);
    // Load data on mount
    const loadConversationRef = React.useRef(loadConversation);
    React.useEffect(()=>{
        loadConversationRef.current = loadConversation;
    }, [
        loadConversation
    ]);
    React.useEffect(()=>{
        let cancelled = false;
        let retryTimeoutId = null;
        let retryCount = 0;
        const maxRetries = 3;
        const bootstrap = async ()=>{
            try {
                const savedModel = localStorage.getItem('chat-model');
                if (savedModel) {
                    dispatch({
                        type: 'SET_MODEL',
                        payload: savedModel
                    });
                }
                const savedPrompt = localStorage.getItem('chat-system-prompt');
                if (savedPrompt) {
                    dispatch({
                        type: 'SET_SYSTEM_PROMPT',
                        payload: savedPrompt
                    });
                }
                dispatch({
                    type: 'SET_LOADING',
                    payload: true
                });
                const summaries = await aiServerClient.listConversations();
                if (cancelled) return;
                const promptForMapping = savedPrompt ?? undefined;
                const mapped = summaries.map((dto)=>mapConversationSummary(dto, promptForMapping)).sort((a, b)=>b.updated_at.getTime() - a.updated_at.getTime());
                dispatch({
                    type: 'SET_CHATS',
                    payload: mapped
                });
                if (mapped.length > 0) {
                    const initialChatId = mapped[0].id;
                    dispatch({
                        type: 'SELECT_CHAT',
                        payload: initialChatId
                    });
                    void loadConversationRef.current?.(initialChatId);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('Failed to load conversations:', error);
                    // 404 에러나 네트워크 에러인 경우 재시도 제한
                    const isNetworkError = error instanceof Error && (error.message.includes('404') || error.message.includes('Network'));
                    if (isNetworkError && retryCount < maxRetries) {
                        retryCount++;
                        console.log(`Retrying conversation load... (attempt ${retryCount}/${maxRetries})`);
                        // 지수 백오프: 2초, 4초, 8초
                        const retryDelay = Math.pow(2, retryCount) * 1000;
                        retryTimeoutId = setTimeout(()=>{
                            if (!cancelled) {
                                bootstrap();
                            }
                        }, retryDelay);
                    } else {
                        console.log('Stopping conversation load retries. Max retries reached or non-network error.');
                    }
                }
            } finally{
                if (!cancelled) {
                    dispatch({
                        type: 'SET_LOADING',
                        payload: false
                    });
                }
            }
        };
        bootstrap();
        return ()=>{
            cancelled = true;
            if (retryTimeoutId) {
                clearTimeout(retryTimeoutId);
            }
        };
    }, []);
    const value = {
        ...state,
        createChat,
        deleteChat,
        clearChatMessages,
        selectChat,
        updateChatTitle,
        clearChats,
        sendMessage,
        regenerateMessage,
        deleteMessage,
        stopGeneration,
        setModel,
        setSystemPrompt,
        getCurrentChat,
        getAllChats,
        exportChat,
        importChat
    };
    return /*#__PURE__*/ _jsx(ChatContext.Provider, {
        value: value,
        children: children
    });
}
// Hook to use chat context
export function useChat() {
    const context = useContext(ChatContext);
    if (context === undefined) {
        throw new Error('useChat must be used within a ChatProvider');
    }
    return context;
}
